import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './SyncedRouteMap.css';

const SyncedRouteMap = ({ 
  telemetryData, 
  currentTime, 
  onLocationClick,
  dataOffset = 0
}) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const pathLayerRef = useRef(null);
  const markerRef = useRef(null);

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || !telemetryData || telemetryData.length === 0) return;
    if (mapInstanceRef.current) return; // Already initialized

    // Filter data with valid GPS coordinates
    const gpsData = telemetryData.filter(d => d.lat && d.lon);
    if (gpsData.length === 0) return;

    // Initialize Leaflet map
    const map = L.map(mapRef.current).setView([gpsData[0].lat, gpsData[0].lon], 14);
    
    // Add satellite tile layer
    L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
      attribution: 'Esri'
    }).addTo(map);

    // Draw path
    const coordinates = gpsData.map(p => [p.lat, p.lon]);
    const path = L.polyline(coordinates, {
      color: '#6a5cb8',
      weight: 3,
      opacity: 0.8
    }).addTo(map);

    // Create current position marker
    const marker = L.circleMarker(coordinates[0], {
      radius: 8,
      color: '#6a5cb8',
      fillColor: '#fff',
      fillOpacity: 1,
      weight: 3
    }).addTo(map);

    // Fit map to path bounds
    map.fitBounds(path.getBounds());

    // Add click handler for seeking
    if (onLocationClick) {
      map.on('click', (e) => {
        // Find closest point on path
        const clickedLatLng = e.latlng;
        let closestIndex = 0;
        let minDistance = Infinity;
        
        gpsData.forEach((point, index) => {
          const distance = map.distance(clickedLatLng, [point.lat, point.lon]);
          if (distance < minDistance) {
            minDistance = distance;
            closestIndex = index;
          }
        });
        
        const closestPoint = gpsData[closestIndex];
        if (closestPoint && closestPoint.time !== undefined) {
          onLocationClick(closestPoint.time);
        }
      });
    }

    mapInstanceRef.current = map;
    pathLayerRef.current = path;
    markerRef.current = marker;

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [telemetryData, onLocationClick]);

  // Update marker position based on currentTime
  useEffect(() => {
    if (!telemetryData || !markerRef.current || currentTime === undefined) return;

    // Apply data offset: if currentTime is 10s and dataOffset is 3s, look for data at 7s
    const adjustedTime = Math.max(0, currentTime - dataOffset);

    // Find closest telemetry point for adjusted time
    const closestPoint = telemetryData.reduce((prev, curr) => {
      return Math.abs(curr.time - adjustedTime) < Math.abs(prev.time - adjustedTime) ? curr : prev;
    });

    if (closestPoint && closestPoint.lat && closestPoint.lon) {
      markerRef.current.setLatLng([closestPoint.lat, closestPoint.lon]);
    }
  }, [currentTime, telemetryData, dataOffset]);

  return <div ref={mapRef} className="synced-route-map" id="synced-map"></div>;
};

export default SyncedRouteMap;

